import React from "react";
import GoogleMapReact from 'google-map-react';
import chile from '../core/data/chile.json';
import Region from "./Region";
import HistoricalDetails from './HistoricalDetails';

const colors = [
  "#FF0000", // Red
  "#FFA500", // Orange
  "#FFFF00", // Yellow
  "#008000", // Green
  "#0000FF", // Blue
  "#4B0082", // Indigo
  "#EE82EE", // Violet
  "#800000", // Maroon
  "#FFC0CB", // Pink
  "#800080", // Purple
  "#008080", // Teal
  "#00FFFF", // Cyan
  "#000080", // Navy
  "#FFD700", // Gold
  "#808000", // Olive
  "#A52A2A" // Brown
];

export default function Map(){
  const [selectedRegion, setSelectedRegion] = React.useState(null);
  const [regionMetadata, setRegionMetadata] = React.useState(null);
  const [regionMostProbableDisaster, setRegionMostProbableDisaster] = React.useState(null);
  const [isHistoricalDetailsOpen, setIsHistoricalDetailsOpen] = React.useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = React.useState(false);

  const defaultProps = {
    center: {
      lat: -33.4087987,
      lng: -70.5715482
    },
    zoom: 6
  }

  React.useEffect(() => {
    const calculateMetadataByRegion = (region) => {
      if (!region.disasters) {
        return null;
      }
  
      const metadata = region.disasters.reduce((acc, disaster) => {
        acc.totalDeaths = (acc.totalDeaths || 0) + (disaster.totalDeaths || 0);
        acc.noInjured = (acc.noInjured || 0) + (disaster.noInjured || 0);
        acc.noAffected = (acc.noAffected || 0) + (disaster.noAffected || 0);
        acc.noHomeless = (acc.noHomeless || 0) + (disaster.noHomeless || 0);
        acc.totalAffected = (acc.totalAffected || 0) + (disaster.totalAffected || 0);
        acc.insuredDamages = (acc.insuredDamages || 0) + (disaster["insuredDamages ('000 US$)"] || 0);
        acc.totalDamages = (acc.totalDamages || 0) + (disaster["totalDamages ('000 US$)"] || 0);
        return acc;
      }, {
        totalDeaths: 0,
        noInjured: 0,
        noAffected: 0,
        noHomeless: 0,
        totalAffected: 0,
        insuredDamages: 0,
        totalDamages: 0,
      });
  
      return metadata;
    }

    if (selectedRegion) {
      setRegionMetadata(calculateMetadataByRegion(selectedRegion));
    }
  }, [selectedRegion]);

  React.useEffect(() => {
    function mostProbableDisaster (disasters) {
      let disasterCounts = {}
      let latestDisasters = {}

      for (let disaster of disasters) {
        let subGroup = disaster['subGroup']
        if (!disasterCounts[subGroup]) {
          disasterCounts[subGroup] = 0
        }
        disasterCounts[subGroup]++

        // Parse the date and keep track of the most recent date for each subGroup
        let date = new Date(disaster['startDate'])
        if (
          !latestDisasters[subGroup] ||
          date > latestDisasters[subGroup]['date']
        ) {
          latestDisasters[subGroup] = { date: date, disaster: disaster }
        }
      }

      // Find the maximum count and all disaster types with that count
      let maxCount = Math.max(...Object.values(disasterCounts))
      let maxDisasters = Object.keys(disasterCounts).filter(
        disaster => disasterCounts[disaster] === maxCount
      )

      // If there's only one disaster with the max count, return it
      if (maxDisasters.length === 1) {
        return maxDisasters[0]
      }

      // If there's more than one, return the most recent one
      let mostRecentDisaster = maxDisasters.reduce((mostRecent, current) => {
        return latestDisasters[current]['date'] >
          latestDisasters[mostRecent]['date']
          ? current
          : mostRecent
      })

      return mostRecentDisaster
    }

    if (selectedRegion) {
      setRegionMostProbableDisaster(mostProbableDisaster(selectedRegion.disasters));
    }
  }, [selectedRegion])

  const handleApiLoaded = ({ map, maps }) => {
    // Use map and maps objects
    let data = new maps.Data()
    data.addGeoJson(chile)
    data.setMap(map)

    data.setStyle((feature) => {
      const color = colors[feature.h.region_id - 1]
      return {
        fillColor: color,
        strokeColor: color,
        strokeWeight: 1
      }
    })

    data.addListener('click', (event) => {
      console.log(event.feature.h.details)
      console.log(event.feature.h.region_id)
      setSelectedRegion(event.feature.h)
      setIsRegionModalOpen(true);
    })
  }   

  const showHistoricalDetailsModal = () => {
    if (selectedRegion) {
      setIsHistoricalDetailsOpen(true);

    }
  }

  const closeRegionModal = () => {
    setSelectedRegion(null);
    setIsRegionModalOpen(false);
  }

  return (
    // Important! Always set the container height explicitly
    <div style={{ height: '100vh', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
        defaultCenter={defaultProps.center}
        defaultZoom={defaultProps.zoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
      >
      </GoogleMapReact>
      <Region
        isOpen={isRegionModalOpen}
        regionName={selectedRegion?.details}
        regionMetadata={regionMetadata}
        regionMostProbableDisaster={regionMostProbableDisaster}
        onClose={closeRegionModal}
        showHistoricalDetailsModal={showHistoricalDetailsModal}
      />
      <HistoricalDetails
        isOpen={isHistoricalDetailsOpen}
        onClose={() => setIsHistoricalDetailsOpen(false)}
        regionData={ selectedRegion }
      />
    </div>
  );
}